import { Injectable } from '@angular/core';
import { getAuth, signInAnonymously, UserCredential } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { Observable, firstValueFrom } from 'rxjs';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import type { FirebaseStorage } from 'firebase/storage';
// models
import { AnswersData } from 'src/app/models/answers-data';
import { PreviewData } from 'src/app/models/preview-data';
import { TemporaryAnswers } from 'src/app/models/temporary-answers';
import { HttpClient } from '@angular/common/http';
import { captureMessage } from '@sentry/angular';
import { environment } from 'src/environments/environment';
import { FirebaseLoaderService } from './firebase-loader.service';
import { StorageManagerService } from './storage-manager.service';


@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  private firestorePromise: Promise<Firestore> | null = null;
  private storagePromise: Promise<FirebaseStorage> | null = null;

  constructor(
    private firebaseLoader: FirebaseLoaderService,
    private http: HttpClient,
    private storageManagerService: StorageManagerService) {
  }

  // Caches for performance optimization
  private authCache: { data: UserCredential; ttl: number } | null = null;
  private docCache = new Map<string, { data: Record<string, unknown> | null; ttl: number }>();
  private functionCache = new Map<string, { data: any; ttl: number }>();
  private fileCache = new Map<string, { data: unknown; ttl: number }>();

  private readonly CACHE_TTL = 10 * 60 * 1000; // 10 minutes for Firebase operations
  private readonly FUNCTION_CACHE_TTL = 5 * 60 * 1000;
  private readonly FILE_PERSIST_TTL = 10 * 60 * 1000; // 10 minutes for file cache persistence
  private readonly cacheableFunctions = new Set<string>([
    'getSurveyProperties',
    'getSurveyTemplateProperties',
    'getSurveys',
    'getPartialAnswers',
    'getOtpCode'
  ]);

  private async getFirestoreWithModule(): Promise<{ firestore: Firestore; mod: typeof import('firebase/firestore') }> {
    if (!this.firestorePromise) {
      this.firestorePromise = this.firebaseLoader.getFirestore();
    }
    const [firestore, mod] = await Promise.all([
      this.firestorePromise,
      import('firebase/firestore')
    ]);
    return { firestore, mod };
  }

  private async getStorage(): Promise<FirebaseStorage> {
    if (!this.storagePromise) {
      this.storagePromise = this.firebaseLoader.getStorage();
    }
    return this.storagePromise;
  }

  private async getFunctionsModule(): Promise<typeof import('firebase/functions')> {
    return import('firebase/functions');
  }
  // initAppCheck() {
  //   return new Promise((resolve, reject) => {

  //     if (this.appCheck == null) {
  //       this.appCheck = initializeAppCheck(getApp(), {
  //         provider: new ReCaptchaV3Provider(environment.recaptcha.siteKey),
  //         isTokenAutoRefreshEnabled: true
  //       })
  //       resolve(true)
  //     } else resolve(true)

  //   })
  // }
  /**
   * Authenticate anonymous users
   * @return Anonymous user credentials
   */
  AnonymousAuthentication(): Promise<UserCredential> {
    // Check cache first
    if (this.authCache && Date.now() < this.authCache.ttl) {
      return Promise.resolve(this.authCache.data);
    }

    const auth = getAuth();
    return signInAnonymously(auth).then((result) => {
      // Cache the result
      this.authCache = { data: result, ttl: Date.now() + this.CACHE_TTL };
      return result;
    });
  }
  /**
   * Function to retry an operation returning a promise when encountering the "deadline-exceeded" error.
   * 
   * @template T - Type of value the promise should return.
   * @param {(...args: any[]) => Promise<T>} fn - Function that returns a promise which is intended to be retried.
   * @param {...any[]} args - Arguments for the 'fn' function.
   * @returns {Promise<T | null>} - Returns a Promise that resolves with the result of 'fn', or null if all attempts fail.
   * 
   * @example
   * async function example() {
   *     const result = await retryOnDeadlineExceeded(this.createDoc.bind(this), data, collectionName);
   *     console.log(result);
   * }
   * 
   * @date 29-07-2023
   * @author Daniel Garcia
   */
  async errorHandler<T>(fn_name: string, ...args: any[]): Promise<T | null> {
    let retries = 3;
    
    while (retries > 0) {
        try {
            const result = await this[fn_name](...args);
            return result;  // if successful, return the result
        } catch (error) {
          
            if (environment.sentryEnabled) {
                captureMessage(String(JSON.stringify(error)));
            }
            // If the error is 'deadline-exceeded' and there are retries left, continue
            if (error.message?.includes("deadline-exceeded") && retries > 0) {
                retries--;
                const delayDuration = retries === 2 ? 0 : (3 - retries) * 5 * 1000;
                await new Promise(resolve => setTimeout(resolve, delayDuration));
            } else {
                // If the error is not 'deadline-exceeded' or there are no retries left, return null
                return null;
            }
        }
    }

    return null;  // if all retries exhausted
  }
  /**
   * Create a document
   * @param previewData 
   * @param collection_ref_name 
   * @returns 
   */
  async createDoc(data: Record<string, unknown>, collectionName: string): Promise<unknown> {
    const { firestore, mod } = await this.getFirestoreWithModule();
    const collectionRef = mod.collection(firestore, collectionName);
    return mod.addDoc(collectionRef, data);
  }
  /**
   * Create or Update document, depends if reference exists
   * @param data 
   * @param collection_ref_name 
   */
  async setDoc(data: Record<string, unknown>, path: string, segments_path: Array<string>): Promise<string> {
    const { firestore, mod } = await this.getFirestoreWithModule();
    const doc_ref = mod.doc(firestore, `${path}`, ...segments_path);
    await mod.setDoc(doc_ref, data, { merge: true });
      // Clear document cache for this path
    const cacheKey = `${path}${segments_path.join('_')}`;
    this.docCache.delete(cacheKey);
    return doc_ref.id;

  }
  /**
   * Get data of a doc
   * @param property property name for where clausule
   * @param value property value to compare
   * @param collection_name collection name
   * @returns 
   * @date 2022-12-28
   * @author Daniel García
   */
  getDoc(property: string, value: string | number, collection_name: string): Observable<PreviewData[] | AnswersData[] | TemporaryAnswers[]> {
    return new Observable(observer => {
      let unsubscribe: (() => void) | null = null;
      this.getFirestoreWithModule()
        .then(({ firestore, mod }) => {
          const q = mod.query(mod.collection(firestore, collection_name), mod.where(property, "==", value));
          unsubscribe = mod.onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(docSnap => ({
              id: docSnap.id,
              ...docSnap.data()
            })) as PreviewData[] | AnswersData[] | TemporaryAnswers[];
            observer.next(data);
          }, (error) => observer.error(error));
        })
        .catch((error) => observer.error(error));
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    });
  }
  /**
   * Listen to a document with onSnapshot() method.
   * @param id document uid
   * @param collection_name 
   * @returns  document data
   */
  async getDocByUid(id: string, collection_name: string): Promise<Record<string, unknown> | null> {
    // Create cache key
    const cacheKey = `${collection_name}_${id}`;
    
    // Check cache
    const cached = this.docCache.get(cacheKey);
    if (cached && Date.now() < cached.ttl) {
      return cached.data;
    }

    const { firestore, mod } = await this.getFirestoreWithModule();
    const docRef = mod.doc(firestore, collection_name, ...[id]);
    const docSnap = await mod.getDoc(docRef);

    const data = docSnap.exists() ? (docSnap.data() as Record<string, unknown>) : null;

    // Cache the result
    this.docCache.set(cacheKey, { data, ttl: Date.now() + this.CACHE_TTL });

    return data;
  }

  /**
	 * @author Matías Villar
	 * @date 07-12-2023
	 * @description Get a document by id and return an observable.
	 */
  getDocByIdObservable<T extends Record<string, unknown>>(id: string, collection_name: string): Observable<T | null> {
    return new Observable(observer => {
      let unsubscribe: (() => void) | null = null;
      this.getFirestoreWithModule()
        .then(({ firestore, mod }) => {
          unsubscribe = mod.onSnapshot(mod.doc(firestore, collection_name, id), (docSnap) => {
            observer.next((docSnap.data() as T | undefined) ?? null);
          });
        })
        .catch((error) => observer.error(error));
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    });
  }


  /**
   * update document data of any collection
   * @param uid id document
   * @param new_data new data to set, could be anything
   * @param collection collection name/route
   * @returns firestore response
   * @date 2022-12-28
   * @author Daniel García
   */
  async updateDoc(id: string, new_data: Record<string, unknown>, collection_name: string): Promise<void> {
    const { firestore, mod } = await this.getFirestoreWithModule();
    const doc_ref = mod.doc(
      firestore,
      `${collection_name}/${id}`
    );
    await mod.updateDoc(doc_ref, new_data);
      // Clear document cache for this document
    const cacheKey = `${collection_name}_${id}`;
    this.docCache.delete(cacheKey);
  }
  /**
   * @author Daniel García
   * @date 2023-06-08
   * delete a document depends on uid
   * @param uid 
   * @param collection_name 
   * 
   */
  async deleteDoc(uid: string, collection_name: string): Promise<void> {
    const { firestore, mod } = await this.getFirestoreWithModule();
    const doc_ref = mod.doc(
      firestore,
      `${collection_name}/${uid}`
    );
    await mod.deleteDoc(doc_ref);
      // Clear document cache for this document
    const cacheKey = `${collection_name}_${uid}`;
    this.docCache.delete(cacheKey);
  }

  /**
   * Call any firebase function
   * @param fn_name 
   * @param data 
   * @returns Promise<any>
   * @date 10-01-2023
   * @author Daniel García
   */
  callFunction<T>(fn_name: string, data: Record<string, unknown>): Promise<{ data: T }> {
    // Create cache key from function name and data
    const shouldCache = this.cacheableFunctions.has(fn_name);
    const cacheKey = `${fn_name}_${JSON.stringify(data)}`;
    
    // Check cache (read-only functions only)
    if (shouldCache) {
      const cached = this.functionCache.get(cacheKey);
      if (cached && Date.now() < cached.ttl) {
        return Promise.resolve(cached.data);
      }
    }

    return new Promise<{ data: T }>(async (resolve, reject) => {
      try {
      const functions = await this.firebaseLoader.getFunctions();
      const { httpsCallable } = await this.getFunctionsModule();
      const exec = httpsCallable(functions, fn_name);
      //console.log(data);

        exec(data).then((result) => {
        // console.log("result", result);
        // Cache the result for read-only functions
        if (shouldCache) {
          this.functionCache.set(cacheKey, { data: result, ttl: Date.now() + this.FUNCTION_CACHE_TTL });
        }
        resolve(result as { data: T });
        }, error => {
        // console.log("error", error);
        reject(error);
        })
      } catch (error) {
        reject(error);
      }
    })
  }
  /**
   * 
   * @param file_name name + extension
   * @param file file to upload
   */
  uploadFile(folder: string, file_name: string, file: File): Promise<{ Location: string }> {
    return new Promise(async (resolve, reject) => {
      try {
        const storage = await this.getStorage();
        const storageRef = ref(storage, folder + file_name);
        const uploadTask = uploadBytesResumable(storageRef, file);

        // Listen for state changes, errors, and completion of the upload.
        uploadTask.on('state_changed',
          (snapshot) => {
            // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
            // const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            // console.log('Upload is ' + progress + '% done');
          },
          (error) => {
            // A full list of error codes is available at
            // https://firebase.google.com/docs/storage/web/handle-errors
            switch (error.code) {
              case 'storage/unauthorized':
                // User doesn't have permission to access the object
                break;
              case 'storage/canceled':
                // User canceled the upload
                break;
              case 'storage/unknown':
                // Unknown error occurred, inspect error.serverResponse
                break;
            }
            reject(error);
          },
          () => {
            // Upload completed successfully, now we can get the download URL
            getDownloadURL(uploadTask.snapshot.ref).then((downloadURL: string) => {
              // console.log('File available at', downloadURL);
              resolve({ "Location": downloadURL });
            });
          }
        );
      } catch (error) {
        reject(error);
      }
    });

  }
  /**
   * Delete file in Storage depends on route and file name
   * @param folder 
   * @param file_name 
   */
  deleteFile(folder: string, file_name: string): Promise<{ status: number; message: string } | null> {
    return new Promise(async (resolve) => {

      try {
        const storage = await this.getStorage();
        const desertRef = ref(storage, `${folder}${file_name}`);
        // Delete the file
        deleteObject(desertRef).then(() => {
          // console.log(`file deleted: ${folder}${file_name}`);
          resolve({ status: 200, message: `file deleted: ${folder}${file_name}` });
        }, () => resolve(null)).catch(() => resolve(null));
      } catch (error) {
        resolve(null);
      }
    });

  }

  getFile(folder: string, file_name: string): Promise<unknown> {
    // Create cache key
    const cacheKey = `${folder}${file_name}`;
    
    // Check cache
    const cached = this.fileCache.get(cacheKey);
    if (cached && Date.now() < cached.ttl) {
      return Promise.resolve(cached.data);
    }

    return new Promise(async (resolve) => {

      try {
        // Check persistent cache (storage) before network
        const persistentKey = `file-cache-${cacheKey}`;
        try {
          const persisted = await this.storageManagerService.getData<unknown>(persistentKey);
          if (persisted != null) {
            this.fileCache.set(cacheKey, { data: persisted, ttl: Date.now() + this.CACHE_TTL });
            resolve(persisted);
            return;
          }
        } catch (error) {
          // Ignore persistent cache failures and continue to network
        }

        const storage = await this.getStorage();
        const desertRef = ref(storage, `${folder}${file_name}`);
        // Get download URL
        getDownloadURL(desertRef).then(async (url: string) => {
          try {
            const file = await firstValueFrom(this.http.get(url));
            // Cache the file contents (not the URL) to avoid extra round-trips
            this.fileCache.set(cacheKey, { data: file, ttl: Date.now() + this.CACHE_TTL });
            // Persist cache with TTL (best effort)
            void this.storageManagerService.storeData(
              persistentKey,
              file,
              this.FILE_PERSIST_TTL
            ).catch(() => undefined);
            resolve(file);
          } catch (error) {
            resolve(null);
          }
        }).catch(() => resolve(null));
      } catch (error) {
        resolve(null);
      }
    });
  }

  /**
   * Clear all caches
   */
  clearCaches(): void {
    this.authCache = null;
    this.docCache.clear();
    this.functionCache.clear();
    this.fileCache.clear();
  }
}
